import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  Stack,
  Autocomplete,
  Chip,
  IconButton,
} from '@mui/material';
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useGetList,
} from 'react-admin';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  AttachFile as AttachIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { UserAutocomplete } from './shared/UserAutocomplete';
import { getDefaultStatusForWorkflow } from '../types/workflow.types';
import { isVirtualTask, getParentRecurrenceTaskID } from '../utils/taskUtils';

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  task?: any; // If provided, we are in EDIT mode
  initialData?: any; // For CREATE mode pre-filling
  workflowId?: string | null; // For CREATE mode
  customerId?: string; // For CREATE mode
  onSuccess?: () => void;
  onDelete?: () => void;
  onSwitchTask?: (newTask: any) => void;
  onMaterialized?: (newTask: any) => void;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  task,
  initialData,
  workflowId,
  customerId,
  onSuccess,
  onDelete,
  onSwitchTask,
  onMaterialized,
}) => {
  const isEditMode = !!task;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Form State
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    priority: string;
    statusId: string;
    scheduledDate: Date | null;
    estimatedHours: string | number;
    assigneeIds: string[];
    workflowId: string;
    categoryId: string;
    customerId: string;
    customerLocationId: string;
    recurrenceFrequency: string;
    recurrenceEndDate: Date | null;
  }>({
    title: '',
    description: '',
    priority: 'Medium',
    statusId: '',
    scheduledDate: null,
    estimatedHours: '',
    assigneeIds: [],
    workflowId: '',
    categoryId: '',
    customerId: '',
    customerLocationId: '',
    recurrenceFrequency: '',
    recurrenceEndDate: null as Date | null,
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Confirmation Dialog State
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmLabel?: string;
    onConfirm: () => Promise<void>;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: async () => { },
  });

  // File Upload State
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Virtual Task State
  const [showVirtualPrompt, setShowVirtualPrompt] = useState(false);
  const [isMaterializing, setIsMaterializing] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string>(''); // To track which ID we are actually updating (virtual or parent)

  // Load Data
  const { data: users } = useGetList('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const { data: allWorkflows = [] } = useGetList('workflows', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Filter active workflows for Create mode, but allow inactive for Edit if the task is already in it
  const workflows = useMemo(() => {
    if (isEditMode) return allWorkflows;
    return allWorkflows.filter((w: any) => w.isActive !== false);
  }, [allWorkflows, isEditMode]);

  const { data: customers = [] } = useGetList('customers', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'displayName', order: 'ASC' },
  });

  // Derived Data
  const selectedCustomer = customers.find((c: any) => c.id === formData.customerId);
  const customerLocations = selectedCustomer?.locations || [];

  const selectedWorkflow = useMemo(() => {
    return workflows.find((w: any) => w.id === formData.workflowId);
  }, [workflows, formData.workflowId]);

  const sortedStatuses = useMemo(() => {
    if (!selectedWorkflow?.statuses) return [];
    return [...selectedWorkflow.statuses].sort((a: any, b: any) => a.orderIndex - b.orderIndex);
  }, [selectedWorkflow]);

  const taskCategories = useMemo(() => {
    if (!selectedWorkflow?.categories) return [];
    return [...selectedWorkflow.categories].sort((a: any, b: any) => a.orderIndex - b.orderIndex);
  }, [selectedWorkflow]);

  // Initialization
  useEffect(() => {
    if (open) {
      // Reset virtual task state to prevent stale state from persisting
      setShowVirtualPrompt(false);
      setIsMaterializing(false);

      if (isEditMode && task) {
        if (isVirtualTask(task)) {
          setShowVirtualPrompt(true);
          setActiveTaskId(task.id);
        } else {
          setActiveTaskId(task.id);
        }

        // EDIT MODE INITIALIZATION
        let scheduledDateObj: Date | null = null;
        if (task.scheduledDate) {
          const date = new Date(task.scheduledDate);
          if (!isNaN(date.getTime())) {
            scheduledDateObj = date;
          }
        }

        // Determine assignees
        let initialAssigneeIds: string[] = [];
        if (task.assigneeIds && Array.isArray(task.assigneeIds)) {
          initialAssigneeIds = task.assigneeIds;
        } else if (task.assignees && Array.isArray(task.assignees)) {
          initialAssigneeIds = task.assignees.map((u: any) => u.id);
        } else if (task.assigneeId) {
          initialAssigneeIds = [task.assigneeId];
        } else if (task.assignee?.id) {
          initialAssigneeIds = [task.assignee.id];
        }

        setFormData({
          title: task.title || '',
          description: task.description || '',
          priority: task.priority || 'Medium',
          statusId: task.statusId || task.workflowStatus?.id || '',
          scheduledDate: scheduledDateObj,
          estimatedHours: task.estimatedHours || '',
          assigneeIds: initialAssigneeIds,
          workflowId: task.workflowId || task.workflow?.id || '',
          categoryId: task.categoryId || task.category?.id || '',
          customerId: task.customerId || task.customer?.id || '',
          customerLocationId: task.customerLocationId || task.customerLocation?.id || '',
          recurrenceFrequency: task.recurrenceFrequency || '',
          recurrenceEndDate: task.recurrenceEndDate ? new Date(task.recurrenceEndDate) : null,
        });
        setExistingAttachments(task.attachments || []);
        setSelectedFiles([]);
      } else {
        // CREATE MODE INITIALIZATION
        const initWorkflowId = workflowId || initialData?.workflowId || (workflows.length > 0 ? workflows[0].id : '');
        const initWorkflow = workflows.find((w: any) => w.id === initWorkflowId);
        const initStatusId = initialData?.statusId || (initWorkflow ? (getDefaultStatusForWorkflow(initWorkflow)?.id || initWorkflow.statuses[0]?.id) : '');

        // Default category
        let initCategoryId = initialData?.categoryId || '';
        if (!initCategoryId && initWorkflow?.categories?.length > 0) {
          // Sort categories to find the first one
          const sortedCats = [...initWorkflow.categories].sort((a: any, b: any) => a.orderIndex - b.orderIndex);
          initCategoryId = sortedCats[0].id;
        }

        setFormData({
          title: initialData?.title || '',
          description: initialData?.description || '',
          priority: initialData?.priority || 'Medium',
          statusId: initStatusId,
          scheduledDate: initialData?.scheduledDate ? new Date(initialData.scheduledDate) : null,
          estimatedHours: initialData?.estimatedHours || '',
          assigneeIds: initialData?.assigneeIds || (initialData?.assigneeId ? [initialData.assigneeId] : []),
          workflowId: initWorkflowId,
          categoryId: initCategoryId,
          customerId: customerId || initialData?.customerId || '',
          customerLocationId: initialData?.customerLocationId || '',
          recurrenceFrequency: initialData?.recurrenceFrequency || '',
          recurrenceEndDate: initialData?.recurrenceEndDate ? new Date(initialData.recurrenceEndDate) : null,
        });
        setSelectedFiles([]);
        setExistingAttachments([]);
      }
    }
  }, [open, task, initialData, workflowId, customerId, workflows, isEditMode]);

  // Handlers
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      notify('Title is required', { type: 'warning' });
      return;
    }
    if (!formData.workflowId) {
      notify('Workflow is required', { type: 'warning' });
      return;
    }
    if (!formData.statusId) {
      notify('Status is required', { type: 'warning' });
      return;
    }
    if (!formData.categoryId) {
      notify('Category is required', { type: 'warning' });
      return;
    }

    if (formData.recurrenceFrequency && formData.recurrenceEndDate && formData.scheduledDate) {
      const startDate = new Date(formData.scheduledDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(formData.recurrenceEndDate);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        notify('Recurrence end date cannot be before scheduled date', { type: 'warning' });
        return;
      }
    }

    try {
      setSaving(true);
      const payload: any = {
        ...formData,
      };

      // Data transformation
      if (payload.estimatedHours) {
        payload.estimatedHours = parseFloat(payload.estimatedHours);
      }

      if (payload.scheduledDate instanceof Date) {
        payload.scheduledDate = payload.scheduledDate.toISOString();
      } else if (!payload.scheduledDate) {
        payload.scheduledDate = null;
      }

      if (payload.recurrenceEndDate instanceof Date) {
        payload.recurrenceEndDate = payload.recurrenceEndDate.toISOString();
      } else if (!payload.recurrenceEndDate) {
        payload.recurrenceEndDate = null;
      }

      // Clean empty strings to null
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });

      // Handle File Uploads for BOTH Create and Edit modes
      const attachmentIds: string[] = [];

      // 1. Add existing attachments (that haven't been removed)
      if (isEditMode) {
        existingAttachments.forEach(att => {
          attachmentIds.push(att.fileId);
        });
      }

      // 2. Upload new files
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          try {
            const timestamp = Date.now();
            const randomSuffix = Math.random().toString(36).substring(2, 8);
            const fileExtension = file.name.split('.').pop();
            const baseFileName = file.name.replace(/\.[^/.]+$/, '');
            const uniqueFileName = `${baseFileName}_${timestamp}_${randomSuffix}.${fileExtension}`;

            const fileData = {
              file: file,
              name: uniqueFileName,
              folder: '/files/tasks',
              description: uniqueFileName,
              mimeType: file.type,
              isPublic: false,
            };
            const { data: uploadedFile } = await dataProvider.create('files', { data: fileData });
            if (uploadedFile && uploadedFile.id) {
              attachmentIds.push(uploadedFile.id);
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            notify(`Failed to upload ${file.name}`, { type: 'warning' });
          }
        }
      }

      // UPDATE
      if (isEditMode) {
        const updateData: any = { ...payload, updatedAt: new Date().toISOString() };

        // Always send attachmentIds if we are in edit mode to ensure sync
        // If attachmentIds is empty, it means all attachments were removed
        updateData.attachmentIds = attachmentIds;

        if (isMaterializing) {
          // Materialize: Update the virtual task which triggers materialization in backend
          // We must ensure recurrence fields are NULL so the new task is not recurring
          updateData.recurrenceFrequency = null;
          updateData.recurrenceEndDate = null;

          // Call update on the virtual ID. The backend should handle this by creating a new task
          // and returning it.
          const { data: materializedTask } = await dataProvider.update('tasks', {
            id: activeTaskId,
            data: updateData,
            previousData: task,
          });

          notify('Task instance created successfully', { type: 'success' });

          if (materializedTask && materializedTask.id !== activeTaskId) {
            if (onMaterialized) {
              onMaterialized(materializedTask);
            }
          }
        } else {
          // Normal update
          await dataProvider.update('tasks', {
            id: activeTaskId,
            data: updateData,
            previousData: task,
          });
          notify('Task updated successfully', { type: 'success' });
        }
      } else {
        // CREATE
        if (attachmentIds.length > 0) {
          payload.attachmentIds = attachmentIds;
        }

        await dataProvider.create('tasks', { data: payload });
        notify('Task created successfully', { type: 'success' });
      }

      refresh();
      handleClose();
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error(error);
      notify(`Failed to ${isEditMode ? 'update' : 'create'} task`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setConfirmationState({
      open: true,
      title: 'Delete Task',
      description: 'Are you sure you want to delete this task? This action cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          setDeleting(true);
          await dataProvider.delete('tasks', { id: task.id, previousData: task });
          notify('Task deleted successfully', { type: 'success' });
          refresh();
          handleClose();
          if (onDelete) onDelete();
        } catch (error) {
          notify('Failed to delete task', { type: 'error' });
        } finally {
          setDeleting(false);
          setConfirmationState(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const handleClose = () => {
    onClose();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return { backgroundColor: '#dc2626', color: 'white' };
      case 'High': return { backgroundColor: '#ea580c', color: 'white' };
      case 'Medium': return { backgroundColor: '#2563eb', color: 'white' };
      case 'Low': return { backgroundColor: '#10b981', color: 'white' };
      default: return {};
    }
  };

  if (isEditMode && !task) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{isEditMode ? 'Edit Task' : 'Create New Task'}</Typography>
            <Box>
              {isEditMode && (
                <IconButton
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  color="error"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Customer & Location */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option: any) => option.displayName || option.company || ''}
                value={customers.find((c: any) => c.id === formData.customerId) || null}
                onChange={(_, newValue) => {
                  const firstLocationId = newValue?.locations?.[0]?.id || '';
                  setFormData({
                    ...formData,
                    customerId: newValue?.id || '',
                    customerLocationId: firstLocationId
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    fullWidth
                    placeholder="Select customer"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Location"
                fullWidth
                value={formData.customerLocationId}
                onChange={(e) => setFormData({ ...formData, customerLocationId: e.target.value })}
                disabled={!formData.customerId}
              >
                <MenuItem value="">None</MenuItem>
                {customerLocations.map((loc: any) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.location?.name || loc.location?.streetLine1 || 'Location ' + loc.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Title & Description */}
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief description of the task"
                autoFocus={!isEditMode}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed information about what needs to be done"
              />
            </Grid>

            {/* Workflow, Status, Priority, Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Workflow"
                fullWidth
                required
                value={formData.workflowId}
                onChange={(e) => {
                  const newWorkflowId = e.target.value;
                  const newWorkflow = workflows.find((w: any) => w.id === newWorkflowId);
                  const defaultStatus = getDefaultStatusForWorkflow(newWorkflow);

                  // Find default category
                  let defaultCategory = '';
                  if (newWorkflow?.categories?.length > 0) {
                    const sortedCats = [...newWorkflow.categories].sort((a: any, b: any) => a.orderIndex - b.orderIndex);
                    defaultCategory = sortedCats[0].id;
                  }

                  setFormData({
                    ...formData,
                    workflowId: newWorkflowId,
                    statusId: defaultStatus?.id || (newWorkflow?.statuses?.[0]?.id) || '',
                    categoryId: defaultCategory,
                  });
                }}
              >
                {workflows.map((w: any) => (
                  <MenuItem key={w.id} value={w.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: w.color,
                        }}
                      />
                      {w.name}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Status"
                fullWidth
                required
                value={formData.statusId}
                onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                disabled={!formData.workflowId}
              >
                {sortedStatuses.map((status: any) => (
                  <MenuItem key={status.id} value={status.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: status.color,
                        }}
                      />
                      {status.name}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Priority"
                fullWidth
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                {['Low', 'Medium', 'High', 'Urgent'].map((p) => (
                  <MenuItem key={p} value={p}>
                    <Chip label={p} size="small" sx={getPriorityColor(p)} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Category"
                fullWidth
                required
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                disabled={!formData.workflowId}
              >
                <MenuItem value="">None</MenuItem>
                {taskCategories.map((cat: any) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 1: Duration and Assign To */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Estimated Duration (hours)"
                type="number"
                fullWidth
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <UserAutocomplete
                multiple
                value={formData.assigneeIds}
                onChange={(userIds) => setFormData({ ...formData, assigneeIds: (userIds as string[]) || [] })}
                label="Assign To"
                fullWidth
              />
            </Grid>

            {/* Row 2: Scheduled Date, Recurrence Frequency, Recurrence End Date */}
            <Grid item xs={12} sm={4}>
              <DateTimePicker
                label="Scheduled Date & Time"
                value={formData.scheduledDate}
                onChange={(newValue) => {
                  let updates: any = { scheduledDate: newValue };
                  if (!newValue) {
                    updates.recurrenceFrequency = '';
                    updates.recurrenceEndDate = null;
                  }
                  setFormData({ ...formData, ...updates });
                }}
                slotProps={{
                  actionBar: { actions: ['clear', 'cancel', 'accept'] },
                  textField: { fullWidth: true }
                }}
              />
            </Grid>

            {(!isEditMode || !task || !isVirtualTask(task)) && !isMaterializing && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Recurrence Schedule"
                    fullWidth
                    value={formData.recurrenceFrequency}
                    onChange={(e) => setFormData({ ...formData, recurrenceFrequency: e.target.value })}
                    disabled={!formData.scheduledDate}
                  >
                    <MenuItem value="">None (One-time)</MenuItem>
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                    <MenuItem value="Yearly">Yearly</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Recurrence End Date"
                      value={formData.recurrenceEndDate}
                      onChange={(newValue) => setFormData({ ...formData, recurrenceEndDate: newValue })}
                      disabled={!formData.recurrenceFrequency}
                      minDate={formData.scheduledDate || undefined}
                      slotProps={{
                        textField: { fullWidth: true },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}

            {/* File Upload - Both Create and Edit Mode */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachIcon fontSize="small" />
                  Attachments (Optional)
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      setSelectedFiles(prev => [...prev, ...Array.from(files)]);
                    }
                  }}
                />

                <Button
                  variant="outlined"
                  startIcon={<AttachIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ mt: 1, mb: (selectedFiles.length > 0 || existingAttachments.length > 0) ? 2 : 0 }}
                >
                  Select Files
                </Button>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {/* Existing Attachments */}
                  {existingAttachments.map((att, index) => (
                    <Chip
                      key={`existing-${att.id}`}
                      label={att.name || att.file?.name || `Attachment ${att.id}`}
                      onDelete={() => setExistingAttachments(prev => prev.filter((_, i) => i !== index))}
                      deleteIcon={<CloseIcon />}
                      variant="filled" // Distinguish existing
                      size="small"
                      sx={{ maxWidth: 200 }}
                    />
                  ))}

                  {/* New Files */}
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={`new-${index}`}
                      label={file.name}
                      onDelete={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                      deleteIcon={<CloseIcon />}
                      variant="outlined"
                      size="small"
                      sx={{ maxWidth: 200 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving || deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || deleting}
          >
            {saving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Task')}
          </Button>
        </DialogActions>

        {/* Virtual Task Action Prompt */}
        <Dialog open={showVirtualPrompt} onClose={handleClose} maxWidth="xs">
          <DialogTitle>{'Edit a repeating task?'}</DialogTitle>
          <DialogContent>
            <Typography>
              {'Do you want to change only this occurrence of the task, or modify the original parent task?'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={async () => {
                setShowVirtualPrompt(false);
                setIsMaterializing(true);
                // We keep the dialog open, but now we are in "Materializing" mode.
                // The user can edit fields. When they save, we handle it.
              }}
            >
              {'Edit only This Task'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={async () => {
                try {
                  // Fetch parent task to pass to callback
                  const parentId = getParentRecurrenceTaskID(task.id);
                  if (!parentId) {
                    notify('Invalid parent task ID', { type: 'error' });
                    return;
                  }

                  const { data: parentTask } = await dataProvider.getOne('tasks', { id: parentId });

                  if (onSwitchTask) {
                    onSwitchTask(parentTask);
                    // handleClose();
                    return;
                  }
                } catch (e) {
                  notify('Failed to load parent task', { type: 'error' });
                } finally {
                  setShowVirtualPrompt(false);
                }
              }
              }
            >
              {'Edit Parent Task'}
            </Button>
            <Button onClick={() => { setShowVirtualPrompt(false); handleClose(); }} color="inherit">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog >

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationState.open}
        onClose={() => !deleting && setConfirmationState(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {confirmationState.title}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmationState.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmationState(prev => ({ ...prev, open: false }))}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmationState.onConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : (confirmationState.confirmLabel || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
